import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import {
    fetchPatientsGetById,
    fetchPatientsResolve,
} from '~/backend/connector-next-api/connector-next-components';
import { flattenSeries } from '~/containers/patient-list/model';
import { useSessionOrNull } from '~/contexts/SessionContext';
import type { Image3D } from '~/domain/patient/image3d';
import {
    createImageListItemView,
    createStructureSetListItemView,
} from '~/domain/projections/factories';
import type { StructureSet } from '~/domain/patient/structureset';
import type { PatientSession } from '~/domain/session/PatientSession';
import { saveOfferState, type SaveOffer } from '~/state/atoms/saveAtoms';
import { handleError } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';
import { TaskRefusedError } from './utils';
import { findImageBySeriesUid } from '~/hooks/workflows/findImageBySeriesUid';

/** The datasets one task reads, all of one patient. */
export type TaskInputs = {
    /** Patient ID from the DICOM data; the connector resolves it to its record. */
    readonly patientId: string;
    /** Series the connector must hold when the task starts. */
    readonly seriesInstanceUids: readonly string[];
    /**
     * Series the task retrieves from a DICOM peer when the connector does not
     * hold them. Only a series that exists nowhere but in the browser is
     * refused.
     */
    readonly retrievableSeriesInstanceUids?: readonly string[];
    readonly structureSetSopInstanceUids?: readonly string[];
};

/** The inputs as the task must name them when it is submitted. */
export type CheckedTaskInputs = {
    /** The input series the connector holds. */
    readonly heldSeriesInstanceUids: ReadonlySet<string>;
    /**
     * The SOP Instance UID the patient database stores for an input structure
     * set, given the UID the task was asked to read. A save of edited content
     * stores the set as a new SOP instance, so the UID the session knows the
     * set by can name an instance the database no longer holds. A structure
     * set the session does not hold is returned as given.
     */
    readonly storedStructureSetUid: (sopInstanceUid: string) => string;
};

/** What the user can do about an input image the connector does not hold. */
type MissingReason = 'needsSave' | 'cannotSave' | 'needsImport';

/**
 * The fixed text of a refusal, free of patient data. It is thrown, and the
 * console mirror of a warning toast records it in place of the labels that
 * the toast shows.
 */
const UNSAVED_INPUT = 'A task input has unsaved changes.';
const MISSING_INPUT = 'A task input is not in the patient database.';
const NOT_SAVED = 'The task inputs were not saved.';

const UNNAMED_IMAGE = 'an unnamed input image';
const UNNAMED_STRUCTURE_SET = 'an unnamed structure set';
const listFormat = new Intl.ListFormat('en', { type: 'conjunction' });

/**
 * The labels as a list that opens a sentence. Only placeholder text is
 * capitalised: a dataset label is shown exactly as the dataset list shows it.
 */
function sentenceSubject(labels: readonly string[]): string {
    const [first, ...rest] = labels;
    const opening =
        first === UNNAMED_IMAGE || first === UNNAMED_STRUCTURE_SET
            ? first.charAt(0).toUpperCase() + first.slice(1)
            : first;
    return listFormat.format([opening, ...rest]);
}

function imageLabel(image: Image3D | undefined): string {
    return (image && createImageListItemView(image).label) || UNNAMED_IMAGE;
}

function missingImageMessage(
    reason: MissingReason,
    labels: readonly string[]
): string {
    const subject = sentenceSubject(labels);
    const one = labels.length === 1;
    const verb = one ? 'is' : 'are';
    switch (reason) {
        case 'needsSave':
            return (
                `${subject} ${verb} not in the patient database. Save ` +
                `${one ? 'it' : 'them'}, then start the task.`
            );
        case 'cannotSave':
            return (
                `${subject} ${verb} not in the patient database, and this ` +
                'patient was not opened from the patient database, so ' +
                `${one ? 'it' : 'they'} cannot be saved. Open the patient ` +
                'from the patient list, then start the task.'
            );
        case 'needsImport':
            return (
                `${subject} ${verb} not in the patient database. Import ` +
                `${one ? 'it' : 'them'} into the patient database, then ` +
                'start the task.'
            );
    }
}

/**
 * The Series Instance UIDs the connector holds for a patient, or undefined
 * when it holds no such patient. The connector keys patients by its own id,
 * so the DICOM Patient ID is resolved first.
 *
 * @throws If the connector cannot be asked.
 */
async function seriesHeldFor(
    dicomPatientId: string
): Promise<ReadonlySet<string> | undefined> {
    const resolution = await fetchPatientsResolve({
        body: { dicomPatientId },
    });
    if (!resolution.exists || !resolution.patientId) {
        return undefined;
    }
    const detail = await fetchPatientsGetById({
        pathParams: { id: resolution.patientId },
    });
    return new Set(
        flattenSeries(detail).flatMap((series) =>
            series.seriesInstanceUid ? [series.seriesInstanceUid] : []
        )
    );
}

/** Why a save is offered: one sentence per input that only a save can fix. */
function saveOfferMessage(
    unsaved: readonly string[],
    notStored: readonly string[]
): string {
    const sentences: string[] = [];
    if (unsaved.length > 0) {
        const has = unsaved.length === 1 ? 'has' : 'have';
        sentences.push(`${sentenceSubject(unsaved)} ${has} unsaved changes.`);
    }
    if (notStored.length > 0) {
        const is = notStored.length === 1 ? 'is' : 'are';
        sentences.push(
            `${sentenceSubject(notStored)} ${is} not in the patient database.`
        );
    }
    sentences.push(
        'The task reads its inputs from the patient database. Saving stores ' +
            'every change listed here, and then the task starts.'
    );
    return sentences.join(' ');
}

/**
 * One pass of the check that {@link useCheckTaskInputs} describes. A pass
 * that may offer the save and gets it runs once more, without the offer.
 */
async function checkOnce(
    session: PatientSession | null,
    offerSave: (request: Omit<SaveOffer, 'resolve'>) => Promise<boolean>,
    {
        patientId,
        seriesInstanceUids,
        retrievableSeriesInstanceUids = [],
        structureSetSopInstanceUids = [],
    }: TaskInputs,
    mayOfferSave: boolean
): Promise<CheckedTaskInputs> {
    const held = await seriesHeldFor(patientId).catch(
        (error: unknown): never => {
            handleError(error);
            enqueueSnackbar(
                'The patient database cannot be reached, so the ' +
                    'task cannot check its input. Try again.',
                { variant: 'error' }
            );
            throw new TaskRefusedError(
                'The connector could not be asked for the task inputs.',
                { cause: error }
            );
        }
    );

    if (held === undefined) {
        enqueueSnackbar(
            'The patient is not in the patient database, so the ' +
                'task cannot use its images. Open the patient from ' +
                'the patient list, then start the task.',
            { variant: 'warning' }
        );
        throw new TaskRefusedError(
            'The task inputs belong to a patient the connector does not hold.'
        );
    }

    // Read after the lookup: an edit made while it ran must count.
    const pendingChanges = session?.pendingChangesStore.getState() ?? null;
    const canSave = session?.patientSaveService.canSave ?? false;
    const imageForSeries = (uid: string): Image3D | undefined =>
        findImageBySeriesUid(session?.patient, uid);

    const structureSets = new Map<string, StructureSet | undefined>(
        structureSetSopInstanceUids.map((uid) => [
            uid,
            session?.patient.getStructureSetWithSopInstanceUid(uid),
        ])
    );
    const unsaved: string[] = [];
    for (const structureSet of structureSets.values()) {
        if (
            structureSet &&
            pendingChanges?.byStructureSet.has(structureSet.id)
        ) {
            unsaved.push(
                createStructureSetListItemView(structureSet).displayName ||
                    UNNAMED_STRUCTURE_SET
            );
        }
    }

    const saveReason: MissingReason = canSave ? 'needsSave' : 'cannotSave';
    const createdHere = (image: Image3D | undefined): boolean =>
        !!image && pendingChanges?.byImage.get(image.id)?.status === 'new';
    const missing = new Map<MissingReason, string[]>();
    const refuse = (
        image: Image3D | undefined,
        reason: MissingReason
    ): void => {
        missing.set(reason, [
            ...(missing.get(reason) ?? []),
            imageLabel(image),
        ]);
    };

    for (const uid of seriesInstanceUids) {
        if (!held.has(uid)) {
            const image = imageForSeries(uid);
            refuse(image, createdHere(image) ? saveReason : 'needsImport');
        }
    }
    for (const uid of retrievableSeriesInstanceUids) {
        const image = imageForSeries(uid);
        if (!held.has(uid) && createdHere(image)) {
            refuse(image, saveReason);
        }
    }

    const notStored = missing.get('needsSave') ?? [];
    const saveClearsEveryBlock =
        !missing.has('cannotSave') && !missing.has('needsImport');
    if (
        (unsaved.length > 0 || notStored.length > 0) &&
        saveClearsEveryBlock &&
        canSave &&
        mayOfferSave
    ) {
        const saved = await offerSave({
            title: 'Save before the task starts',
            message: saveOfferMessage(unsaved, notStored),
            saveLabel: 'Save and start',
        });
        if (!saved) {
            throw new TaskRefusedError(NOT_SAVED);
        }
        return checkOnce(
            session,
            offerSave,
            {
                patientId,
                seriesInstanceUids,
                retrievableSeriesInstanceUids,
                structureSetSopInstanceUids,
            },
            false
        );
    }

    if (unsaved.length > 0) {
        const subject = sentenceSubject(unsaved);
        const has = unsaved.length === 1 ? 'has' : 'have';
        enqueueSnackbar(
            canSave
                ? `${subject} ${has} unsaved changes. Save, then ` +
                      'start the task.'
                : `${subject} ${has} changes that cannot be saved, ` +
                      'because this patient was not opened from the ' +
                      'patient database. Open the patient from the ' +
                      'patient list, then start the task.',
            { variant: 'warning', logMessage: UNSAVED_INPUT }
        );
        throw new TaskRefusedError(UNSAVED_INPUT);
    }

    if (missing.size > 0) {
        enqueueSnackbar(
            [...missing]
                .map(([reason, labels]) => missingImageMessage(reason, labels))
                .join(' '),
            { variant: 'warning', logMessage: MISSING_INPUT }
        );
        throw new TaskRefusedError(MISSING_INPUT);
    }

    const storedUids = new Map(
        [...structureSets].map(([uid, structureSet]) => [
            uid,
            structureSet?.storedSopInstanceUid ?? uid,
        ])
    );
    return {
        heldSeriesInstanceUids: new Set(
            [...seriesInstanceUids, ...retrievableSeriesInstanceUids].filter(
                (uid) => held.has(uid)
            )
        ),
        storedStructureSetUid: (uid) => storedUids.get(uid) ?? uid,
    };
}

/**
 * Checks that a task can read its inputs from the connector.
 *
 * A task reads the connector's copy of each input. An input structure set
 * with unsaved changes therefore blocks the task, which would otherwise run
 * on superseded contours. Changes to datasets the task does not read do not
 * matter. The connector must also hold each input series; a retrievable
 * series may be absent unless only the browser holds it.
 *
 * When a save would clear every blocking input, the user is offered the save
 * of every pending change through {@link saveOfferState}. Only
 * a confirmed save that stored every change lets the task go on, and the
 * inputs are then checked again from the start. Nothing is saved without
 * that confirmation. Any other blocking input refuses the task with a
 * message.
 *
 * Unsaved changes are read when the check runs, not when the hook rendered.
 *
 * @returns A function that resolves with the inputs as the task must name
 *   them. Otherwise it tells the user by label which input blocks the task,
 *   or the user declined the save, and it throws a {@link TaskRefusedError},
 *   so the caller aborts the submission. The thrown message carries no
 *   patient data. A connector that cannot be asked is also a refusal, never
 *   a missing input; its lookup error is reported to error tracking and kept
 *   as the cause.
 */
export default function useCheckTaskInputs() {
    const session = useSessionOrNull();
    const setSaveOffer = useSetRecoilState(saveOfferState);

    return useCallback(
        (inputs: TaskInputs): Promise<CheckedTaskInputs> => {
            const offerSave = (request: Omit<SaveOffer, 'resolve'>) =>
                new Promise<boolean>((resolve) =>
                    setSaveOffer((open) => {
                        open?.resolve(false);
                        return { ...request, resolve };
                    })
                );
            return checkOnce(session, offerSave, inputs, true);
        },
        [session, setSaveOffer]
    );
}
